import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Heart, Menu, Sparkles, X, Youtube } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import useTipper from "@/hooks/use-tipper";

const NAV_ITEMS = [
  { to: "/", label: "Home", end: true },
  { to: "/archive", label: "Archive" },
  { to: "/collections", label: "Collections" },
  { to: "/pet-rescue-saga", label: "Pet Rescue" },
  { to: "/harry-potter-puzzles-spells", label: "Harry Potter" },
  { to: "/support", label: "Support" },
  { to: "/about", label: "About" },
];

export default function Header({ channel }) {
  const tipper = useTipper();
  const [open, setOpen] = useState(false);

  const channelHref = channel?.handle
    ? `https://www.youtube.com/${channel.handle}`
    : "https://www.youtube.com/@tobiasdeamon";

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-xl bg-[#090a0f]/80 border-b border-white/10"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-3 group min-w-0" data-testid="header-archive-logo">
          <span className="relative flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-md border border-[#d95d39]/40 transition-transform group-hover:scale-105">
            <img
              src="/logo192.png"
              alt="tobiasdeamon ghost logo"
              width="40"
              height="40"
              data-testid="header-ghost-logo"
              className="w-full h-full rounded-md object-cover"
            />
            <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#d95d39] pulse-copper"></span>
          </span>
          <div className="leading-tight min-w-0">
            <div className="hidden sm:block font-mono-tech text-[10px] tracking-[0.28em] text-[#d95d39] truncate">
              THE ARCHIVE // {channel?.subscriber_count ? new Intl.NumberFormat().format(channel.subscriber_count) : "—"} SUBS
            </div>
            <div className="font-serif-editorial text-lg sm:text-xl leading-none tracking-tight text-[#f4f1ea] truncate">
              {channel?.title || "tobiasdeamon"} <span className="text-[#d95d39]">·</span> Vault
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-1 text-sm">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-md whitespace-nowrap font-mono-tech uppercase text-[11px] tracking-[0.2em] transition-colors ${
                  isActive
                    ? "text-[#f4f1ea] bg-white/5"
                    : "text-white/60 hover:text-[#f4f1ea] hover:bg-white/5"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/support"
            data-testid={tipper ? "header-supporter-badge" : "header-support-btn"}
            className={`hidden md:inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-mono-tech tracking-widest uppercase transition-colors ${
              tipper
                ? "bg-[#d95d39]/10 border border-[#d95d39]/60 text-[#d95d39]"
                : "border border-[#d95d39]/50 text-[#d95d39] hover:bg-[#d95d39] hover:text-white"
            }`}
          >
            {tipper ? <Sparkles className="w-3.5 h-3.5" /> : <Heart className="w-3.5 h-3.5" />}
            {tipper ? "Supporter" : "Tip"}
          </Link>
          <a
            href={channelHref}
            target="_blank"
            rel="noreferrer"
            data-testid="header-youtube-link"
            className="hidden sm:inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-[#d95d39] text-white text-xs font-mono-tech tracking-widest uppercase hover:bg-[#c14e2e] transition-colors"
          >
            <Youtube className="w-4 h-4" />
            Channel
          </a>

          {/* Mobile menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="header-menu-btn"
                className="xl:hidden w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white/70 hover:text-[#d95d39] hover:border-[#d95d39] transition-colors"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="bg-[#0c0e15] border-l border-white/10 text-[#f4f1ea] w-[85vw] max-w-[360px] p-0 flex flex-col"
              data-testid="mobile-menu"
            >
              <div className="flex items-center justify-between p-5 border-b border-white/10">
                <div className="mono-label text-[#d95d39]">The Archive · Menu</div>
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  data-testid="mobile-menu-close"
                  className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-white/70 hover:text-[#d95d39] hover:border-[#d95d39]"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <nav className="flex-1 px-3 py-4 space-y-1">
                {NAV_ITEMS.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
                    className={({ isActive }) =>
                      `block px-4 py-3.5 rounded-lg font-serif-editorial text-xl transition-colors ${
                        isActive
                          ? "bg-white/[0.04] text-[#d95d39]"
                          : "text-[#f4f1ea] hover:bg-white/[0.03] hover:text-[#d95d39]"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </nav>
              <div className="p-5 border-t border-white/10 space-y-2.5">
                <Link
                  to="/support"
                  onClick={() => setOpen(false)}
                  data-testid={tipper ? "mobile-supporter-badge" : "mobile-support-btn"}
                  className={`w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-xs font-mono-tech tracking-widest uppercase transition-colors ${
                    tipper
                      ? "bg-[#d95d39]/10 border border-[#d95d39]/60 text-[#d95d39]"
                      : "border border-[#d95d39]/50 text-[#d95d39] hover:bg-[#d95d39] hover:text-white"
                  }`}
                >
                  {tipper ? <Sparkles className="w-3.5 h-3.5" /> : <Heart className="w-3.5 h-3.5" />}
                  {tipper ? "Supporter · ads off" : "Tip via PayPal"}
                </Link>
                <a
                  href={channelHref}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="mobile-youtube-link"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#d95d39] text-white text-xs font-mono-tech tracking-widest uppercase hover:bg-[#c14e2e] transition-colors"
                >
                  <Youtube className="w-4 h-4" />
                  Open YouTube channel
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
